import { Battery, Power, Sliders, Sparkles } from "lucide-react";
import type { ControllerMode } from "@/data/types";
import { MODE_CATALOG, MODE_TONE, ORDERED_MODES } from "@/data/modeCatalog";
import { cn } from "@/lib/cn";

/**
 * Mode selector — a row of chips, one per available mode. Used inside the
 * EntryEditor, the DefaultEditor and the OverridePanel; reads from the
 * single ORDERED_MODES catalog so adding a mode to the SPA only touches
 * one file.
 *
 * Icons mirror ems-admin's ControllerSchedulePage for visual continuity
 * across the two SPAs.
 */
const ICONS: Record<ControllerMode, React.ComponentType<{ className?: string }>> = {
  gridZeroControl: Sparkles,
  PeakShaving: Sliders,
  ConstantPower: Battery,
  OFF: Power,
};

export function ModePicker({
  value,
  onChange,
  disabled,
}: {
  value: ControllerMode;
  onChange: (m: ControllerMode) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {ORDERED_MODES.map((m) => {
        const Icon = ICONS[m];
        const active = value === m;
        return (
          <button
            key={m}
            type="button"
            disabled={disabled}
            onClick={() => onChange(m)}
            title={MODE_CATALOG[m].summary}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed",
              active
                ? `${MODE_TONE[m]} ring-1`
                : "border-edge text-text-muted hover:border-edge-strong hover:bg-surface-2",
            )}
          >
            <Icon className="h-3 w-3" />
            {MODE_CATALOG[m].label}
          </button>
        );
      })}
    </div>
  );
}
